openapi: "3.0.0"
info:
  version: "0.9.2"
  title: "CityBase Checkout API Documentation"
  description: "API documentation for CityBase Standard Checkout.
  Note that __all payloads should be JSON format only__.
  Also note that __all field names must be in snake case only, including custom attributes__.
  "
servers:
  - url: "https://invoice-service-product.dev.cityba.se/invoices"
    description: "Standard Checkout"
paths:
  /{client_identifier}/{service_type}:
    post:
      consumes:
        - application/x-www-form-urlencoded
      parameters:
        - in: path
          name: client_identifier
          required: true
          schema:
            type: string
            minimum: 1
          default: cityville_parking
          description: Unique way to identify a client. *This value will be configured per integration*.
        - in: path
          name: service_type
          required: true
          schema:
            type: string
            minimum: 1
          default: permits
          description: Description of the debt type. *This value will be configured per integration*.
      summary: "Standard Checkout"
      description: A standard checkout invoice post from a client application that represents the details of a customer's intent to make a payment. CityBase responds with a redirect to a payment form where the customer can complete their purchase.
      requestBody:
        content:
          application/json:
            schema:
              $ref: '#/components/schemas/StandardCheckout'
            examples:
              one_line_item:
                summary: One Line Item
                value:
                  account_id: "4442ABC"
                  allowed_payment_methods:
                    - CARD
                    - BANK
                  line_items:
                    - description: Parking Permit
                      sub_description: Spring 2022
                      amount: 12750
                      custom_attributes:
                        - key: reference_id
                          value: "44203"
                        - key: year
                          value: "2022"
                  cancel_url:
                    url: www.cancel.com
                    label: Cancel
                  return_url:
                    url: www.return.com
                    label: Return
                  custom_attributes:
                    - key: external_request_id
                      value: "123-GHF"
                  expires_at: "2024-10-04T15:00:00Z"
              multiple_line_items:
                summary: Multiple Line Items
                value:
                  account_id: "909SRC"
                  allowed_payment_methods:
                    - CARD
                    - BANK
                  line_items:
                    - description: Parking Permit
                      sub_description: Spring 2022
                      amount: 12750
                      custom_attributes:
                        - key: reference_id
                          value: "44203"
                        - key: year
                          value: "2022"
                    - description: Parking Permit
                      sub_description: Fall 2022
                      amount: 12950
                      custom_attributes:
                        - key: reference_id
                          value: "44227"
                        - key: year
                          value: "2022"
                  cancel_url:
                    url: www.cancel.com
                    label: Cancel
                  return_url:
                    url: www.return.com
                    label: Return
                  custom_attributes:
                    - key: external_request_id
                      value: "124-NVQ"
              one_line_item_200:
                summary: One Line Item - 200 Response
                value:
                  redirect: false
                  account_id: "4442ABC"
                  allowed_payment_methods:
                    - CARD
                    - BANK
                  line_items:
                    - description: Parking Permit
                      sub_description: Spring 2022
                      amount: 12750
                      custom_attributes:
                        - key: reference_id
                          value: "44203"
                        - key: year
                          value: "2022"
                  cancel_url:
                    url: www.cancel.com
                    label: Cancel
                  return_url:
                    url: www.return.com
                    label: Return
                  custom_attributes:
                    - key: external_request_id
                      value: "123-GHF"
      responses:
        '200':
          description: Success. If 'redirect' is set to false.
          content:
            application/json:
              schema:
                type: object
                properties:
                  redirect_uri:
                    type: string
                    description: The URL to be redirected to.
        '303':
          description: Success.
          headers:
            location:
              schema:
                type: string
              description: The URL to be redirected to.
        '404':
          description: Not Found
        '422':
          description: The payment payload is malformed.
        '500':
          description: Unexpected error.
  /visit_reporting_path:
    post:
      summary: "Multi-payment Reporting"
      description: After a POS transaction is complete, a collection of payments can be reported
      requestBody:
        content:
          application/json:
            schema:
              $ref: '#/components/schemas/VisitReporting'
            examples:
              single_tender_cash_example:
                summary: Reporting a single-tender cash POS payment
                value:
                  - data:
                      id: 70000278
                      total_amount: 28.00
                      service_fee: 0.0
                      amount: 28.00
                      payment_source_channel: "cashier"
                      status: "successful"
                      payment_type: "cash"
                      agency: "Cityville Water"
                      voidable: false
                      refundable: false
                      created_at: "2022-09-13T12:02:30Z"
                      line_items:
                        - id: 70000279
                          amount: 2800
                          custom_attributes: {}
                      associated_payments: []
                      custom_attributes:
                        - key: "external_request_id"
                          value: "123-GHF"
              single_tender_credit_card_example:
                summary: Reporting a single-tender credit card POS payment
                value:
                  - data:
                      id: 70000234
                      total_amount: 28.00
                      service_fee: 1.75
                      amount: 26.25
                      payment_source_channel: "cashier"
                      status: "successful"
                      payment_type: "credit_card"
                      agency: "Cityville Water"
                      voidable: true
                      refundable: false
                      credit_card: {
                        last_four: "0000",
                        card_type: "discover"
                      }
                      created_at: "2022-09-10T12:02:30Z"
                      line_items:
                        - id: 70000235
                          amount: 2625
                          custom_attributes:
                            address: "123 fake St."
                            phone: "5553331234"
                      associated_payments: []
                      custom_attributes:
                        - key: "external_request_id"
                          value: "123-GHF"
              single_tender_check_example:
                summary: Reporting a single-tender check POS payment
                value:
                  - data:
                      id: 70000236
                      total_amount: 26.75
                      service_fee: 0.0
                      amount: 26.75
                      payment_source_channel: "cashier"
                      status: "successful"
                      payment_type: "check"
                      agency: "Cityville Water"
                      voidable: true
                      refundable: false
                      credit_card: null
                      bank_account:
                        routing_number: "031000503"
                        bank_account_type: "checking"
                        account_number_last_four: "6770"
                      created_at: "2022-09-10T12:02:30Z"
                      line_items:
                        - id: 70000237
                          amount: 2675
                          custom_attributes:
                            address: "123 fake St."
                            phone: "5553331234"
                      associated_payments: []
                      custom_attributes:
                        - key: "external_request_id"
                          value: "123-GHF"
              multi_tender_example:
                summary: Reporting a multi-tender POS payment
                value:
                  - data:
                      id: 70000234
                      total_amount: 28.00
                      service_fee: 1.75
                      amount: 26.25
                      payment_source_channel: "cashier"
                      status: "successful"
                      payment_type: "credit_card"
                      agency: "Cityville Water"
                      voidable: true
                      refundable: false
                      credit_card: {
                        last_four: "0000",
                        card_type: "discover"
                      }
                      created_at: "2022-09-10T12:02:30Z"
                      line_items:
                        - id: 70000235
                          amount: 2625
                          custom_attributes:
                            address: "123 fake St."
                            phone: "5553331234"
                      associated_payments: []
                      custom_attributes:
                        - key: "external_request_id"
                          value: "123-GHF"
                  - data:
                      id: 70000236
                      total_amount: 26.75
                      service_fee: 0.0
                      amount: 26.75
                      payment_source_channel: "cashier"
                      status: "successful"
                      payment_type: "check"
                      agency: "Cityville Water"
                      voidable: true
                      refundable: false
                      credit_card: null
                      bank_account:
                        routing_number: "031000503"
                        bank_account_type: "checking"
                        account_number_last_four: "6770"
                      created_at: "2022-09-10T12:02:30Z"
                      line_items:
                        - id: 70000237
                          amount: 2675
                          custom_attributes:
                            address: "123 fake St."
                            phone: "5553331234"
                      associated_payments: []
                      custom_attributes:
                        - key: "external_request_id"
                          value: "123-GHF"
                  - data:
                      id: 70000278
                      total_amount: 28.00
                      service_fee: 0.0
                      amount: 28.00
                      payment_source_channel: "cashier"
                      status: "successful"
                      payment_type: "cash"
                      agency: "Cityville Water"
                      voidable: false
                      refundable: false
                      created_at: "2022-09-13T12:02:30Z"
                      line_items:
                        - id: 70000279
                          amount: 2800
                          custom_attributes: {}
                      associated_payments: []
                      custom_attributes:
                        - key: "external_request_id"
                          value: "123-GHF"
  /payment_reporting_path:
    post:
      summary: "Payment Reporting"
      description: Following a successful payment, refund or void, CityBase can send a POST request to a preconfigured URL specified by the client with details of the transaction.
      requestBody:
        content:
          application/json:
            schema:
              $ref: '#/components/schemas/PaymentReporting'
            examples:
              card_example:
                summary: Reporting a Credit Card payment
                value:
                  data:
                    id: 70000234
                    total_amount: 28.00
                    service_fee: 1.75
                    amount: 26.25
                    payment_source_channel: "kiosk"
                    status: "successful"
                    payment_type: "credit_card"
                    agency: "Cityville Water"
                    voidable: true
                    refundable: false
                    credit_card: {
                      last_four: "0000",
                      card_type: "discover"
                    }
                    created_at: "2022-09-10T12:02:30Z"
                    line_items:
                      - id: 70000235
                        amount: 2625
                        custom_attributes:
                          address: "123 fake St."
                          phone: "5553331234"
                    associated_payments: []
                    custom_attributes:
                      - key: "external_request_id"
                        value: "123-GHF"
              check_example:
                summary: Reporting a Check payment
                value:
                  data:
                    id: 70000234
                    total_amount: 26.75
                    service_fee: 0.0
                    amount: 26.75
                    payment_source_channel: "cashier"
                    status: "successful"
                    payment_type: "check"
                    agency: "Cityville Water"
                    voidable: true
                    refundable: false
                    credit_card: null
                    bank_account:
                      routing_number: "031000503"
                      bank_account_type: "checking"
                      account_number_last_four: "6770"
                    created_at: "2022-09-10T12:02:30Z"
                    line_items:
                      - id: 70000235
                        amount: 2675
                        custom_attributes:
                          address: "123 fake St."
                          phone: "5553331234"
                    associated_payments: []
                    custom_attributes:
                      - key: "external_request_id"
                        value: "123-GHF"
              cash_example:
                summary: Reporting a Cash payment
                value:
                  data:
                    id: 70000278
                    total_amount: 28.00
                    service_fee: 0.0
                    amount: 28.00
                    payment_source_channel: "kiosk"
                    status: "successful"
                    payment_type: "cash"
                    agency: "Cityville Water"
                    voidable: false
                    refundable: false
                    created_at: "2022-09-13T12:02:30Z"
                    line_items:
                      - id: 70000279
                        amount: 2800
                        custom_attributes: {}
                    associated_payments: []
                    custom_attributes:
                      - key: "external_request_id"
                        value: "123-GHF"
              token_example:
                summary: Reporting a Token (stored credit card) payment
                value:
                  data:
                    id: 70000234
                    total_amount: 28.00
                    service_fee: 1.75
                    amount: 26.25
                    payment_source_channel: "web"
                    status: "successful"
                    payment_type: "token"
                    agency: "Cityville Water"
                    voidable: true
                    refundable: false
                    credit_card: {
                      last_four: "0000",
                      card_type: "discover"
                    }
                    created_at: "2023-04-26T12:02:30Z"
                    line_items:
                      - id: 70000235
                        amount: 2625
                        custom_attributes:
                          address: "123 fake St."
                          phone: "5553331234"
                    associated_payments: []
                    custom_attributes:
                      - key: "external_request_id"
                        value: "123-GHF"
              partial_refund_card_example:
                summary: Reporting a partial refund payment made by Credit Card for a single line item
                value:
                  data:
                    id: 70000239
                    total_amount: 15.00
                    service_fee: 0.0
                    amount: 15.00
                    payment_source_channel: "web"
                    status: "refunded"
                    payment_type: "credit_card"
                    agency: "Cityville Water"
                    voidable: false
                    refundable: false
                    credit_card: {
                      last_four: "0000",
                      card_type: "discover"
                    }
                    created_at: "2022-09-12T10:01:30Z"
                    line_items:
                      - id: 70000241
                        amount: 2625
                        amount_refunded: 1500
                        amount_refundable: 1125
                        custom_attributes:
                          address: "123 fake St."
                          phone: "5553331234"
                    associated_payments:
                      -  id: 70000234
                         status: "successful"
                    custom_attributes:
                      - key: "external_request_id"
                        value: "123-GHF"
              refund_card_example:
                summary: Reporting a refund payment made by Credit Card
                value:
                  data:
                    id: 70000239
                    total_amount: 26.25
                    service_fee: 0.0
                    amount: 26.25
                    payment_source_channel: "web"
                    status: "refunded"
                    payment_type: "credit_card"
                    agency: "Cityville Water"
                    voidable: false
                    refundable: false
                    credit_card: {
                      last_four: "0000",
                      card_type: "discover"
                    }
                    created_at: "2022-09-12T10:01:30Z"
                    line_items:
                      - id: 70000241
                        amount: 2625
                        amount_refunded: 2625
                        amount_refundable: 0
                        custom_attributes:
                          address: "123 fake St."
                          phone: "5553331234"
                    associated_payments:
                      -  id: 70000234
                         status: "successful"
                    custom_attributes:
                      - key: "external_request_id"
                        value: "123-GHF"
              refund_token_example:
                summary: Reporting a refund payment made by token (stored credit card)
                value:
                  data:
                    id: 70000240
                    total_amount: 26.25
                    service_fee: 0.0
                    amount: 26.25
                    payment_source_channel: "web"
                    status: "refunded"
                    payment_type: "token"
                    agency: "Cityville Water"
                    voidable: false
                    refundable: false
                    credit_card: {
                      last_four: "2234",
                      card_type: "master_card"
                    }
                    created_at: "2023-04-26T20:41:34Z"
                    line_items:
                      - id: 70000241
                        amount: 2625
                        amount_refunded: 2625
                        amount_refundable: 0
                        custom_attributes:
                          address: "123 fake St."
                          phone: "5553331234"
                    associated_payments:
                      -  id: 70000234
                         status: "successful"
                    custom_attributes:
                      - key: "external_request_id"
                        value: "123-GHF"
              partial_refund_check_example:
                summary: Reporting a partial refund payment made by Check for a single line item
                value:
                  data:
                    id: 70000239
                    total_amount: 15.00
                    service_fee: 0.0
                    amount: 15.00
                    payment_source_channel: "kiosk"
                    status: "refunded"
                    payment_type: "check"
                    agency: "Cityville Water"
                    voidable: false
                    refundable: false
                    credit_card: null
                    bank_account:
                      routing_number: "031000503"
                      bank_account_type: "checking"
                      account_number_last_four: "6770"
                    created_at: "2022-09-12T10:01:30Z"
                    line_items:
                      - id: 70000241
                        amount: 2625
                        amount_refunded: 1500
                        amount_refundable: 1125
                        custom_attributes:
                          address: "123 fake St."
                          phone: "5553331234"
                    associated_payments:
                      - id: 70000234
                        status: "successful"
                    custom_attributes:
                      - key: "external_request_id"
                        value: "123-GHF"
              refund_check_example:
                summary: Reporting a refund payment made by Check
                value:
                  data:
                    id: 70000239
                    total_amount: 26.25
                    service_fee: 0.0
                    amount: 26.25
                    payment_source_channel: "kiosk"
                    status: "refunded"
                    payment_type: "check"
                    agency: "Cityville Water"
                    voidable: false
                    refundable: false
                    credit_card: null
                    bank_account:
                      routing_number: "031000503"
                      bank_account_type: "checking"
                      account_number_last_four: "6770"
                    created_at: "2022-09-12T10:01:30Z"
                    line_items:
                      - id: 70000241
                        amount: 2625
                        amount_refunded: 2625
                        amount_refundable: 0
                        custom_attributes:
                          address: "123 fake St."
                          phone: "5553331234"
                    associated_payments:
                      - id: 70000234
                        status: "successful"
                    custom_attributes:
                      - key: "external_request_id"
                        value: "123-GHF"
              void_card_example:
                summary: Reporting a void payment made by Credit Card
                value:
                  data:
                    id: 70101232
                    total_amount: 26.25
                    service_fee: 0.0
                    amount: 26.25
                    payment_source_channel: "cashier"
                    status: "voided"
                    payment_type: "credit_card"
                    agency: "Cityville Water"
                    voidable: false
                    refundable: false
                    credit_card: {
                      last_four: "0000",
                      card_type: "discover"
                    }
                    created_at: "2022-09-12T10:01:30Z"
                    associated_payments: []
                    custom_attributes:
                      - key: "external_request_id"
                        value: "123-GHF"
              void_token_example:
                summary: Reporting a void payment made by token (stored credit card)
                value:
                  data:
                    id: 90101235
                    total_amount: 201.13
                    service_fee: 0.0
                    amount: 201.13
                    payment_source_channel: "web"
                    status: "voided"
                    payment_type: "token"
                    agency: "Cityville Water"
                    voidable: false
                    refundable: false
                    credit_card: {
                      last_four: "2233",
                      card_type: "visa"
                    }
                    created_at: "2023-03-27T11:45:10Z"
                    associated_payments: []
                    custom_attributes:
                      - key: "external_request_id"
                        value: "563-XYZ"
              void_check_example:
                summary: Reporting a void payment made by Check
                value:
                  data:
                    id: 70101232
                    total_amount: 26.25
                    service_fee: 0.0
                    amount: 26.25
                    payment_source_channel: "web"
                    status: "voided"
                    payment_type: "check"
                    agency: "Cityville Water"
                    voidable: false
                    refundable: false
                    bank_account:
                      routing_number: "031000503"
                      bank_account_type: "checking"
                      account_number_last_four: "6770"
                    created_at: "2022-09-12T10:01:30Z"
                    line_items:
                      - id: 70101235
                        amount: 2625
                        custom_attributes:
                          address: "123 fake St."
                          phone: "5553331234"
                    associated_payments: []
                    custom_attributes:
                      - key: "external_request_id"
                        value: "123-GHF"
              void_cash_example:
                summary: Reporting a void payment made by Cash
                value:
                  data:
                    id: 70014406
                    total_amount: 26.25
                    service_fee: 0.0
                    amount: 26.25
                    payment_source_channel: "cashier"
                    status: "voided"
                    payment_type: "cash"
                    agency: "Cityville Water"
                    voidable: false
                    refundable: false
                    bank_account: null
                    created_at: "2023-03-31T10:01:30Z"
                    line_items:
                      - id: 90
                        amount: 2625
                        custom_attributes:
                          address: "123 fake St."
                          phone: "5553331234"
                    associated_payments: []
                    custom_attributes:
                      - key: "external_request_id"
                        value: "123-GHF"
      responses:
        '200-299':
          description: Success.
        '300-599':
          description: Failure. Payment report will retry up to 25 times.
components:
  schemas:
    StandardCheckout:
      type: object
      properties:
        account_id:
          type: string
          description: An ID of an account being paid on.
        allowed_payment_methods:
          type: array
          description: Indicates which payment methods are allowed. "CARD" (Credit and Debit Cards) and "BANK" (Electronic Checks) are supported.
          items:
            type: string
            enum: [CARD, BANK]
        line_items:
          type: array
          description: An Array of Line Items.
          items:
            type: object
            description: An object describing a single Line Item.
            properties:
              description:
                type: string
                description: A String describing the Line Item. This field will be presented to the user during checkout.
              sub_description:
                type: string
                description: An additional way to describe the Line Item. This field will be presented to the user during checkout.
              amount:
                type: integer
                description: The Line Item amount.
              custom_attributes:
                $ref: '#/components/hidden_schemas/CustomAttributes'
            required:
              - description
              - sub_description
              - amount
        cancel_url:
          $ref: '#/components/hidden_schemas/UrlObject'
        return_url:
          $ref: '#/components/hidden_schemas/UrlObject'
        custom_attributes:
          $ref: '#/components/hidden_schemas/CustomAttributes'
        expires_at:
          type: string
          format: date-time
          example: "2024-10-04T15:00:00Z"
          description: |
            An optional date-time string in ISO 8601 format indicating when an invoice should expire.
            Constituents will be unable to pay an expired invoice. By default, _Standard Checkout_
            invoices do not expire. When providing an expiration value, the string must comply with
            one of the following formats:
            1. "YYYY-MM-DD HH:MM:SS" (may also include "T" and/or "Z", such as `2024-10-04T15:00:00Z`)
            2. "YYYY-MM-DD HH:MM:SS.USEC" (may also include "T" and/or "Z", such as `2024-10-04T15:00:00.456Z`)
      required:
        - allowed_payment_methods
        - line_items
        - cancel_url
        - return_url
    PaymentReporting:
      type: object
      properties:
        data:
          type: object
          properties:
            id:
              type: integer
              description: The CityBase internal ID of the payment.
            voidable:
              type: boolean
              description: Indicates whether or not the payment can be voided.
            refundable:
              type: boolean
              description: Indicates whether or not the payment can be refunded.
            total_amount:
              type: number
              description: The amount of the payment with service fee.
            service_fee:
              type: number
              description: The service fee amount.
            amount:
              type: number
              description: The amount of the payment before service fee.
            payment_source_channel:
              type: string
              description: Payment channel of the payment. Options include 'web', 'kiosk', and 'cashier'.
            status:
              type: string
              description: The current status of the payment.
            payment_type:
              type: string
              description: How the payment was made. Options include 'cash', 'check', 'credit_card', and 'token'.
            agency:
              type: string
              description: The name of the agency for which the payment was made.
            associated_payments:
              type: array
              description: An array containing other records associated with the payment. Ex. a refund record.
              items:
                type: object
                properties:
                  id:
                    type: integer
                    description: ID of the associated record.
                  status:
                    type: string
                    description: Status of the associated record.
            credit_card:
              type: object
              description: If payment_type is "credit_card" this object will contain card information. It will be null otherwise.
              properties:
                last_four:
                  type: string
                  description: The last four digits of the credit card number.
                card_type:
                  type: string
                  description: The card type. Ex. visa, discover, etc.
            bank_account:
              type: object
              description: If payment_type is "check" this object will contain bank account information. It will be null otherwise.
              properties:
                routing_number:
                  type: string
                  description: The bank account routing number.
                bank_account_type:
                  type: string
                  description: The bank account type. Ex. checking
                account_number_last_four:
                  type: string
                  description: The last four digits of the bank account number.
            created_at:
              type: string
              description: When the payment was made. Timestamps are in UTC in ISO-8601 format.
            line_items:
              type: array
              description: An Array of Line Items.
              items:
                type: object
                properties:
                  id:
                    type: string
                    description: The ID of the Line Item. Ex. The ID of a Permit being paid for.
                  amount:
                    type: integer
                    description: The Line Item amount.
                  custom_attributes:
                    type: object
                    description: An object containing additional information not handled by other fields.
            required:
             - id
             - amount
             - custom_attributes
            custom_attributes:
                  $ref: '#/components/hidden_schemas/CustomAttributes'
          required:
            - id
            - voidable
            - refundable
            - status
            - amount
            - total_amount
            - service_fee
            - total
            - payment_type
            - agency
            - credit_card
            - bank_account
            - line_items
            - created_at
            - custom_attributes
      required:
        - data
    VisitReporting:
      type: array
      items:
        $ref: '#/components/schemas/PaymentReporting'

  hidden_schemas:
    CustomAttributes:
      type: array
      description: An Array of objects each of which contains keys "key" and "value". This information is NOT presented to the end user, and is only used to pass non-standard data between systems.
      items:
        type: object
        properties:
          key:
            type: string
            description: The key of a key/value pair.
          value:
            type: string
            description: The value of a key/value pair.
        required:
          - key
          - value
    LineItem:
      type: object
      properties:
        id:
          type: string
          description: The ID of the Line Item. Ex. The ID of a Permit being paid for.
        amount:
          type: integer
          description: The Line Item amount.
        description:
          type: string
          description: A String describing the Line Item.
        sub_description:
          type: string
          description: An optional sub-description.
        custom_attributes:
          $ref: '#/components/hidden_schemas/CustomAttributes'
      required:
        - id
        - amount
        - description
        - custom_attributes
    UrlObject:
      type: object
      description: An object describing a URL
      properties:
        url:
          type: string
          description: A URL String
        label:
          type: string
          description: A label associated with the URL. This is used to provide language for a link linking to the URL.
      required:
        - url
        - label
